import { Row, Col } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import CVAccordions from "./components/CVAccordions";
import AddIcon from "@mui/icons-material/Add";
import "./CVform.css";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BackArrow from "../../components/backArrow";
import { FailedNotify, SuccessNotify } from "../../constants/tostify";
import {
  useGetCvFormQuery,
  useGetUserInfoQuery,
  usePostCvFormMutation,
  useUpdateCvFormMutation,
} from "../../features/user/userApiSlice";

const CVForm = () => {
  const { name } = useSelector((state) => state.user);
  const userId =
    name?._id || (typeof name === "string" && name.startsWith("{") ? JSON.parse(name)?._id : name?._id || null);
  const navigate = useNavigate();

  /* ── Fetch existing CV via RTK Query ─────────────────────────────────── */
  const { data: cvDataRes } = useGetCvFormQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  /* ── Fetch fresh user profile info from database ─────────────────────── */
  const { data: userInfoRes } = useGetUserInfoQuery(userId, {
    skip: !userId,
  });

  /* ── RTK Query mutations ──────────────────────────────────────────────── */
  const [postCvForm, { isLoading: isPosting }] = usePostCvFormMutation();
  const [updateCvForm, { isLoading: isUpdating }] = useUpdateCvFormMutation();
  const isloading = isPosting || isUpdating;

  /* ── Local form state ─────────────────────────────────────────────────── */
  const [newdata, setNewdata] = useState({
    user: userId,
    profileInformation: {
      firstName: "",
      lastName: "",
      jobDesignation: "",
      email: "",
      primaryNumber: "",
      secondaryPhone: "",
      addressLine1: "",
      addressLine2: "",
      permanentResidence: "",
      postalcode: "",
      gender: "",
      dob: "",
      cob: "",
      pob: "",
      nationality: "",
      idType: "",
      idNumber: "",
      martialStatus: "",
      visaStatus: "",
      jobTitle: "",
      portfolioLink: "",
      photo: "",
    },
    preemphistory: [
      {
        companyname: "",
        designation: "",
        industry: "",
        startdate: "",
        enddate: "",
      },
    ],
    formaleducation: [
      {
        institutename: "",
        startdate: "",
        enddate: "",
        major: "",
        qualification: "",
        typeofstudy: "",
        degreestatus: "",
      },
    ],
    profesionalskils: [],
    languageskils: [
      {
        language: "",
        speakingproficiency: "",
        writingproficiency: "",
        readingproficiency: "",
      },
    ],
    certification: [
      {
        nameofcertificate: "",
        institution: "",
        issuingauthority: "",
        effectivedate: "",
        expirationdate: "",
      },
    ],
  });

  /* ── Populate form from existing CV data or user profile on load ── */
  useEffect(() => {
    const userObj = userInfoRes?.data?.User || name || {};
    const fullname =
      userObj.fullname || (typeof name === "string" ? name : "") || "";
    const nameParts = fullname.trim().split(" ");
    const defaultFirstName = nameParts[0] || "";
    const defaultLastName = nameParts.slice(1).join(" ") || "";
    const defaultEmail =
      typeof userObj.email === "string"
        ? userObj.email
        : userObj.email?.text || "";
    const defaultPhone =
      typeof userObj.contactNumber === "string"
        ? userObj.contactNumber
        : userObj.contactNumber?.text || "";
    const defaultAddress =
      typeof userObj.address === "string"
        ? userObj.address
        : userObj.address?.text || userObj.address?.street || "";
    const defaultPhoto = Array.isArray(userObj.imageUrl)
      ? userObj.imageUrl[0]
      : typeof userObj.imageUrl === "string"
        ? userObj.imageUrl
        : userObj.profilePic || "";

    if (cvDataRes?.cvform) {
      const cv = cvDataRes.cvform;
      const pi = cv.profileInformation || {};
      setNewdata({
        ...cv,
        user: userId || cv.user,
        profileInformation: {
          firstName: pi.firstName || defaultFirstName,
          lastName: pi.lastName || defaultLastName,
          jobDesignation: pi.jobDesignation || "",
          email: pi.email || defaultEmail,
          primaryNumber: pi.primaryNumber || defaultPhone,
          secondaryPhone: pi.secondaryPhone || "",
          addressLine1: pi.addressLine1 || defaultAddress,
          addressLine2: pi.addressLine2 || "",
          permanentResidence: pi.permanentResidence || defaultAddress,
          postalcode: pi.postalcode || "",
          gender: pi.gender || "",
          dob: pi.dob || "",
          cob: pi.cob || "",
          pob: pi.pob || "",
          nationality: pi.nationality || "",
          idType: pi.idType || "",
          idNumber: pi.idNumber || "",
          martialStatus: pi.martialStatus || "",
          visaStatus: pi.visaStatus || "",
          jobTitle: pi.jobTitle || "",
          portfolioLink: pi.portfolioLink || "",
          photo: pi.photo || defaultPhoto,
        },
        preemphistory: cv.preemphistory?.length
          ? cv.preemphistory
          : [
              {
                companyname: "",
                designation: "",
                industry: "",
                startdate: "",
                enddate: "",
              },
            ],
        formaleducation: cv.formaleducation?.length
          ? cv.formaleducation
          : [
              {
                institutename: "",
                startdate: "",
                enddate: "",
                major: "",
                qualification: "",
                typeofstudy: "",
                degreestatus: "",
              },
            ],
        profesionalskils: cv.profesionalskils || [],
        languageskils: cv.languageskils?.length
          ? cv.languageskils
          : [
              {
                language: "",
                speakingproficiency: "",
                writingproficiency: "",
                readingproficiency: "",
              },
            ],
        certification: cv.certification?.length
          ? cv.certification
          : [
              {
                nameofcertificate: "",
                institution: "",
                issuingauthority: "",
                effectivedate: "",
                expirationdate: "",
              },
            ],
      });
    } else if (defaultFirstName || defaultEmail || defaultPhone || defaultAddress || defaultPhoto) {
      setNewdata((prev) => ({
        ...prev,
        user: userId,
        profileInformation: {
          ...prev.profileInformation,
          firstName: prev.profileInformation.firstName || defaultFirstName,
          lastName: prev.profileInformation.lastName || defaultLastName,
          email: prev.profileInformation.email || defaultEmail,
          primaryNumber: prev.profileInformation.primaryNumber || defaultPhone,
          addressLine1: prev.profileInformation.addressLine1 || defaultAddress,
          permanentResidence:
            prev.profileInformation.permanentResidence || defaultAddress,
          photo: prev.profileInformation.photo || defaultPhoto,
        },
      }));
    }
  }, [cvDataRes, userInfoRes, name, userId]);

  /* ── Certification handler ────────────────────────────────────────────── */
  const handelvalues = (e, index = 0) => {
    const { name: fieldName, value } = e.target;
    const updated = [...newdata.certification];
    updated[index] = { ...updated[index], [fieldName]: value };
    setNewdata((prev) => ({ ...prev, certification: updated }));
  };

  /* ── POST (create) CV ─────────────────────────────────────────────────── */
  const PostCvData = async (e) => {
    e.preventDefault();
    try {
      const res = await postCvForm({ ...newdata, user: name?._id }).unwrap();
      if (res?.message || res?.cvform || res?.success) {
        SuccessNotify("Successfully Posted CV Data");
        navigate("/select-template", { state: { cvform: res?.cvform || newdata } });
      }
    } catch (err) {
      FailedNotify("Failed to Post CV Data");
    }
  };

  /* ── PUT (update) CV ──────────────────────────────────────────────────── */
  const UpdateCvData = async (e) => {
    e.preventDefault();
    try {
      const res = await updateCvForm({ ...newdata, user: name?._id }).unwrap();
      if (res?.message || res?.cvform || res?.success) {
        SuccessNotify("Successfully Updated CV Data");
        navigate("/select-template", { state: { cvform: res?.cvform || newdata } });
      }
    } catch (err) {
      FailedNotify("Failed to Update CV Data");
    }
  };

  return (
    <Row>
      <form>
        <div className="screenHeight d-flex justify-content-center align-items-center">
          <Col lg={8} md={9} className="Form-Shadow">
            <div className="arrowback">
              <BackArrow back="/SellingSide" black="black" mystyle="p-1 ps-3" />
            </div>

            <Col md={11} lg={11} className="mx-auto px-3">
              <div className="d-flex justify-content-center pt-3 pb-5">
                <div className="d-flex align-content-center gap-3">
                  <AddIcon className="iconWhite plus-icon mt-2" />
                  <p className="color-one mt-1">Add Information</p>
                </div>
                <div className="d-flex align-items-center pt-1 ps-2">
                  <h6 className="titleText p-0">Add Information</h6>
                </div>
              </div>

              <CVAccordions
                one="Profile Information"
                two="Previous Employement History"
                three="Formal Education"
                four="Professional Skills"
                five="Language Skills"
                six="Certifications/Licences"
                newdata={newdata}
                setNewdata={setNewdata}
                handelvalues={handelvalues}
                userId={name?._id}
              />

              <div className="d-flex justify-content-end pt-3 pb-4">
                {newdata?._id ? (
                  <button
                    className="btn-color"
                    onClick={UpdateCvData}
                    disabled={isloading}
                  >
                    {isUpdating ? "Updating…" : "Update CV"}
                  </button>
                ) : (
                  <button
                    className="btn-color"
                    onClick={PostCvData}
                    disabled={isloading}
                  >
                    {isPosting ? "Posting…" : "Post CV"}
                  </button>
                )}
              </div>
            </Col>
          </Col>
        </div>
      </form>
    </Row>
  );
};

export default CVForm;
